/**
 * Keeps the player's calibration samples across sessions. Storage is a
 * per-device convenience: every access is guarded, and the recogniser works
 * normally (built-in templates only) when storage is missing, full, blocked
 * or holds something unreadable.
 */
import type { HandwritingRecognizer } from '../contracts.ts';
import { PointCloudRecognizer } from './recognizer/index.ts';

export const HW_SAMPLES_STORAGE_KEY = 'dropstack.hw.samples';

/** The subset of the Web Storage API we use (lets tests pass an in-memory store). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * window.localStorage when it exists, is reachable and actually implements
 * the Storage methods (Node 25 exposes a method-less `localStorage` global
 * unless started with --localstorage-file); otherwise null.
 */
export function browserStorage(): KeyValueStore | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const s: Partial<KeyValueStore> | null = localStorage;
    const usable =
      !!s && typeof s.getItem === 'function' && typeof s.setItem === 'function' && typeof s.removeItem === 'function';
    return usable ? localStorage : null;
  } catch {
    // Accessing localStorage throws in some privacy modes / sandboxed frames.
    return null;
  }
}

/** Wrap a recogniser so calibration changes are saved to `store` and restored on creation. */
export function createPersistentRecognizer(
  store: KeyValueStore | null,
  inner: HandwritingRecognizer = new PointCloudRecognizer(),
): HandwritingRecognizer {
  try {
    const saved = store?.getItem(HW_SAMPLES_STORAGE_KEY);
    if (saved) inner.importUserSamples(saved);
  } catch {
    // Unreadable or corrupt samples: start from the built-in templates.
  }

  const save = () => {
    if (!store) return;
    try {
      if (inner.userSampleCount() > 0) store.setItem(HW_SAMPLES_STORAGE_KEY, inner.exportUserSamples());
      else store.removeItem(HW_SAMPLES_STORAGE_KEY);
    } catch {
      // Quota exceeded or storage blocked: samples still work for this session.
    }
  };

  return {
    recognize: (strokes) => inner.recognize(strokes),
    addUserSample: (char, strokes) => {
      inner.addUserSample(char, strokes);
      save();
    },
    clearUserSamples: () => {
      inner.clearUserSamples();
      save();
    },
    userSampleCount: () => inner.userSampleCount(),
    exportUserSamples: () => inner.exportUserSamples(),
    importUserSamples: (json) => {
      inner.importUserSamples(json);
      save();
    },
  };
}
